/**
 * Material schema + registry.
 *
 * Two representations, deliberately:
 *   1. `MaterialDef` objects — authoring-friendly, read by UI/codex/worldgen.
 *   2. Flattened typed arrays (`M.density[id]`, `M.tags[id]`, …) — the ONLY
 *      thing hot simulation loops touch. Property lookups on objects inside a
 *      per-cell loop are the difference between 2 ms and 20 ms.
 *
 * Call `finalizeMaterials()` once after all definitions are registered.
 */

export type MatId = number;

// ------------------------------------------------------------------ tags

export const Tag = {
  FLAMMABLE: 1 << 0,
  ACCELERANT: 1 << 1,
  ORGANIC: 1 << 2,
  UNDEAD: 1 << 3,
  METAL: 1 << 4,
  STONE: 1 << 5,
  CERAMIC: 1 << 6,
  CONDUCTIVE: 1 << 7,
  INSULATOR: 1 << 8,
  CORROSIVE: 1 << 9,
  TOXIC: 1 << 10,
  CRYO: 1 << 11,
  PYRO: 1 << 12,
  FUNGAL: 1 << 13,
  NANITE: 1 << 14,
  VOID: 1 << 15,
  TEMPORAL: 1 << 16,
  RADIANT: 1 << 17,
  CURSED: 1 << 18,
  MACHINE: 1 << 19,
  BIOMASS: 1 << 20,
  EXPLOSIVE: 1 << 21,
  SOLUBLE: 1 << 22,
  SLICK: 1 << 23,
  STICKY: 1 << 24,
  POROUS: 1 << 25,
  SEALED: 1 << 26,
  LIVING: 1 << 27,
  BLOOD: 1 << 28,
  CRYSTAL: 1 << 29,
  VOLATILE: 1 << 30,
  SACRED: 1 << 31,
} as const;

export type TagMask = number;
export type TagName = keyof typeof Tag;

export const TAG_NAMES = Object.keys(Tag) as TagName[];

export function tags(...names: TagName[]): TagMask {
  let m = 0;
  for (const n of names) m |= Tag[n];
  // `| 0` keeps it a signed int32 so bitwise tests stay fast and consistent.
  return m | 0;
}

export function hasTag(mask: TagMask, t: number): boolean {
  return (mask & t) !== 0;
}

export function tagNamesOf(mask: TagMask): TagName[] {
  return TAG_NAMES.filter((n) => (mask & Tag[n]) !== 0);
}

// ------------------------------------------------------------- categories

export const Category = {
  VOID: 0,
  RIGID: 1,
  LOOSE: 2,
  LIQUID: 3,
  GAS: 4,
  SLUDGE: 5,
  PLASMA: 6,
  FIELD: 7,
} as const;
export type CategoryId = (typeof Category)[keyof typeof Category];

export const CATEGORY_NAMES = ['void', 'rigid', 'loose', 'liquid', 'gas', 'sludge', 'plasma', 'field'];

/** Categories whose cells participate in gravity-driven movement. */
export function isMobile(cat: CategoryId): boolean {
  return cat >= Category.LOOSE && cat <= Category.PLASMA && cat !== Category.PLASMA
    ? true
    : cat === Category.PLASMA || cat === Category.SLUDGE;
}

/** Blocks entity movement. */
export function isSolidCategory(cat: CategoryId): boolean {
  return cat === Category.RIGID || cat === Category.LOOSE;
}

// -------------------------------------------------------------- one-way

/**
 * Semi-solidity: the direction a one-way surface *faces*, i.e. the side you
 * can land on. `'up'` is the platform case — solid to a body descending onto
 * its top edge, air to everything else.
 *
 * None of our seven categories could express this (docs/10 §2.3), and it is
 * deliberately a property rather than an eighth category: a catwalk is still
 * `RIGID` wood and must keep inheriting wood's reactions. Composition beats a
 * parallel taxonomy here.
 */
export type Direction = 'up' | 'down' | 'left' | 'right';

/** Flattened encoding of `MaterialDef.oneWaySolid`. 0 means "not one-way". */
export const OneWay = {
  NONE: 0,
  UP: 1,
  DOWN: 2,
  LEFT: 3,
  RIGHT: 4,
} as const;
export type OneWayCode = (typeof OneWay)[keyof typeof OneWay];

const ONE_WAY_CODE: Record<Direction, OneWayCode> = {
  up: OneWay.UP,
  down: OneWay.DOWN,
  left: OneWay.LEFT,
  right: OneWay.RIGHT,
};

// ------------------------------------------------------------------ flags

export const Flag = {
  MOVED: 1 << 0,
  WET: 1 << 1,
  BURNING: 1 << 2,
  CHARGED: 1 << 3,
  FROZEN: 1 << 4,
  CORRODING: 1 << 5,
  INFECTED: 1 << 6,
  STATIC: 1 << 7,
  LIT: 1 << 8,
  PRESSURIZED: 1 << 9,
  PHASED: 1 << 10,
  SANCTIFIED: 1 << 11,
  CURSED: 1 << 12,
  VARIANT_SHIFT: 13,
  VARIANT_MASK: 0b111 << 13,
} as const;

// ------------------------------------------------------------------ types

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export interface MaterialDef {
  id: MatId;
  name: string;
  label: string;
  category: CategoryId;
  tags: TagMask;

  // physical
  density: number;
  viscosity: number;
  dispersion: number;
  hardness: number;
  brittleness: number;
  friction: number;
  mass: number;

  // thermal
  temp: number;
  conductivity: number;
  heatCapacity: number;
  ignitionTemp: number | null;
  burnTemp: number;
  burnDuration: number;
  emissive: number;
  meltsAt: number | null;
  meltsInto: string | null;
  freezesAt: number | null;
  freezesInto: string | null;
  boilsAt: number | null;
  boilsInto: string | null;
  condensesAt: number | null;
  condensesInto: string | null;
  ashesInto: string | null;

  // electrical
  electricConductivity: number;
  chargeResistance: number;
  shortCircuits: boolean;

  // chemical / exotic
  corrosiveness: number;
  corrosionResist: number;
  toxicity: number;
  reactivity: number;
  resonance: number;
  bioActivity: number;
  temporalStability: number;
  sanctity: number;

  // structural
  ruptureThreshold: number;
  shatteredInto: string | null;
  indestructible: boolean;
  /**
   * Semi-solid: which face blocks entity movement, or `null` for an ordinary
   * material. `'up'` is a one-way floor — you land on it from above, jump up
   * through it from below, and walk through it sideways.
   *
   * Only entity collision reads this. To the cell sim a one-way material is
   * not solid at all, so liquids, gases and loose matter pass straight
   * through — see the `M.solid` derivation in `finalizeMaterials()`.
   */
  oneWaySolid: Direction | null;
  /**
   * Ticks before the cell dissipates, 0 = permanent. Distinct from
   * `burnDuration`, which is combustion fuel — a spore cloud has a long life
   * and almost no fuel, and conflating the two makes hazard gases vanish.
   */
  lifetime: number;

  // presentation
  color: RGB;
  colorVariance: number;
  grain: number;
  alpha: number;
  refraction: number;

  /** Player-facing codex text. */
  desc: string;
}

/**
 * `color` is omitted from the Partial before being re-added; intersecting a
 * `color?: RGB` with `color: RGB | number` collapses to `RGB & number`.
 */
export type MaterialSpec = Partial<
  Omit<MaterialDef, 'id' | 'name' | 'label' | 'category' | 'color'>
> & {
  /** Either an RGB triple or a 0xRRGGBB literal. */
  color: RGB | number;
};

// --------------------------------------------------------------- registry

const defs: MaterialDef[] = [];
const byName = new Map<string, MatId>();
let finalized = false;

export function rgb(hex: number): RGB {
  return { r: (hex >> 16) & 255, g: (hex >> 8) & 255, b: hex & 255 };
}

const DEFAULTS: Omit<MaterialDef, 'id' | 'name' | 'label' | 'category' | 'color'> = {
  tags: 0,
  density: 1000,
  viscosity: 0,
  dispersion: 0,
  hardness: 3,
  brittleness: 0.3,
  friction: 0.8,
  mass: 1,
  temp: 20,
  conductivity: 0.2,
  heatCapacity: 1,
  ignitionTemp: null,
  burnTemp: 600,
  burnDuration: 120,
  emissive: 0,
  meltsAt: null,
  meltsInto: null,
  freezesAt: null,
  freezesInto: null,
  boilsAt: null,
  boilsInto: null,
  condensesAt: null,
  condensesInto: null,
  ashesInto: null,
  electricConductivity: 0,
  chargeResistance: 0.5,
  shortCircuits: false,
  corrosiveness: 0,
  corrosionResist: 0.3,
  toxicity: 0,
  reactivity: 1,
  resonance: 0,
  bioActivity: 0,
  temporalStability: 1,
  sanctity: 0,
  ruptureThreshold: 400,
  shatteredInto: null,
  indestructible: false,
  oneWaySolid: null,
  lifetime: 0,
  colorVariance: 0.06,
  grain: 0.1,
  alpha: 1,
  refraction: 0,
  desc: '',
};

/**
 * Register a material. Declaration order defines ids; VOID must be first.
 * Returns the assigned id so definitions can reference each other numerically
 * where needed, though string references (resolved in finalize) are preferred.
 */
export function defineMaterial(
  name: string,
  label: string,
  category: CategoryId,
  spec: MaterialSpec,
): MatId {
  if (finalized) throw new Error('defineMaterial after finalizeMaterials()');
  if (byName.has(name)) throw new Error(`duplicate material: ${name}`);
  const id = defs.length;
  const color = typeof spec.color === 'number' ? rgb(spec.color) : spec.color;
  const def: MaterialDef = {
    ...DEFAULTS,
    ...(spec as object),
    color,
    id,
    name,
    label,
    category,
  } as MaterialDef;
  defs.push(def);
  byName.set(name, id);
  return id;
}

export function matId(name: string): MatId {
  const id = byName.get(name);
  if (id === undefined) throw new Error(`unknown material: ${name}`);
  return id;
}

export function tryMatId(name: string): MatId | undefined {
  return byName.get(name);
}

export function materialDef(id: MatId): MaterialDef {
  return defs[id];
}

export function allMaterials(): readonly MaterialDef[] {
  return defs;
}

export function materialCount(): number {
  return defs.length;
}

// -------------------------------------------------------- flattened tables

/**
 * Struct-of-arrays view over the registry. Indexed by MatId.
 * `-1` in a transition field means "no transition".
 */
export const M = {
  category: new Uint8Array(0),
  tags: new Int32Array(0),
  density: new Float32Array(0),
  viscosity: new Float32Array(0),
  dispersion: new Uint8Array(0),
  hardness: new Float32Array(0),
  brittleness: new Float32Array(0),
  friction: new Float32Array(0),
  mass: new Float32Array(0),
  spawnTemp: new Int16Array(0),
  conductivity: new Float32Array(0),
  heatCapacity: new Float32Array(0),
  ignitionTemp: new Int16Array(0),
  burnTemp: new Int16Array(0),
  burnDuration: new Uint16Array(0),
  emissive: new Float32Array(0),
  meltsAt: new Int16Array(0),
  meltsInto: new Int16Array(0),
  freezesAt: new Int16Array(0),
  freezesInto: new Int16Array(0),
  boilsAt: new Int16Array(0),
  boilsInto: new Int16Array(0),
  condensesAt: new Int16Array(0),
  condensesInto: new Int16Array(0),
  ashesInto: new Int16Array(0),
  electricConductivity: new Float32Array(0),
  chargeResistance: new Float32Array(0),
  shortCircuits: new Uint8Array(0),
  corrosiveness: new Float32Array(0),
  corrosionResist: new Float32Array(0),
  toxicity: new Float32Array(0),
  reactivity: new Float32Array(0),
  resonance: new Float32Array(0),
  bioActivity: new Float32Array(0),
  temporalStability: new Float32Array(0),
  sanctity: new Float32Array(0),
  ruptureThreshold: new Float32Array(0),
  shatteredInto: new Int16Array(0),
  indestructible: new Uint8Array(0),
  /** `OneWay` code per material; 0 for the overwhelming majority. */
  oneWay: new Int8Array(0),
  lifetime: new Uint16Array(0),
  alpha: new Float32Array(0),
  grain: new Float32Array(0),
  refraction: new Float32Array(0),
  /** Derived: blocks entity movement. */
  solid: new Uint8Array(0),
  /** Derived: participates in gravity movement. */
  mobile: new Uint8Array(0),
  /** Derived: light opacity 0..1 for the light field. */
  opacity: new Float32Array(0),
  /** Derived: 1 for materials bright enough to seed the light field (§6.3). */
  lightSource: new Uint8Array(0),
  /** RGBA8 palette, 4 bytes per material — uploaded straight to a GL texture. */
  palette: new Uint8Array(0),
};

/** Sentinel: cell id 0 is always empty space. */
export const VOID: MatId = 0;

/** Emissive at or above which a material seeds the light field. See below. */
export const LIGHT_SOURCE_MIN = 0.3;

export function finalizeMaterials(): void {
  if (finalized) return;
  const n = defs.length;
  const alloc = <T>(ctor: new (n: number) => T): T => new ctor(n);

  M.category = alloc(Uint8Array);
  M.tags = alloc(Int32Array);
  M.density = alloc(Float32Array);
  M.viscosity = alloc(Float32Array);
  M.dispersion = alloc(Uint8Array);
  M.hardness = alloc(Float32Array);
  M.brittleness = alloc(Float32Array);
  M.friction = alloc(Float32Array);
  M.mass = alloc(Float32Array);
  M.spawnTemp = alloc(Int16Array);
  M.conductivity = alloc(Float32Array);
  M.heatCapacity = alloc(Float32Array);
  M.ignitionTemp = alloc(Int16Array);
  M.burnTemp = alloc(Int16Array);
  M.burnDuration = alloc(Uint16Array);
  M.emissive = alloc(Float32Array);
  M.meltsAt = alloc(Int16Array);
  M.meltsInto = alloc(Int16Array);
  M.freezesAt = alloc(Int16Array);
  M.freezesInto = alloc(Int16Array);
  M.boilsAt = alloc(Int16Array);
  M.boilsInto = alloc(Int16Array);
  M.condensesAt = alloc(Int16Array);
  M.condensesInto = alloc(Int16Array);
  M.ashesInto = alloc(Int16Array);
  M.electricConductivity = alloc(Float32Array);
  M.chargeResistance = alloc(Float32Array);
  M.shortCircuits = alloc(Uint8Array);
  M.corrosiveness = alloc(Float32Array);
  M.corrosionResist = alloc(Float32Array);
  M.toxicity = alloc(Float32Array);
  M.reactivity = alloc(Float32Array);
  M.resonance = alloc(Float32Array);
  M.bioActivity = alloc(Float32Array);
  M.temporalStability = alloc(Float32Array);
  M.sanctity = alloc(Float32Array);
  M.ruptureThreshold = alloc(Float32Array);
  M.shatteredInto = alloc(Int16Array);
  M.indestructible = alloc(Uint8Array);
  M.oneWay = alloc(Int8Array);
  M.lifetime = alloc(Uint16Array);
  M.alpha = alloc(Float32Array);
  M.grain = alloc(Float32Array);
  M.refraction = alloc(Float32Array);
  M.solid = alloc(Uint8Array);
  M.mobile = alloc(Uint8Array);
  M.opacity = alloc(Float32Array);
  M.lightSource = alloc(Uint8Array);
  M.palette = new Uint8Array(n * 4);

  const ref = (s: string | null): number => {
    if (s === null) return -1;
    const id = byName.get(s);
    if (id === undefined) throw new Error(`material "${s}" referenced but not defined`);
    return id;
  };

  for (let i = 0; i < n; i++) {
    const d = defs[i];
    M.category[i] = d.category;
    M.tags[i] = d.tags | 0;
    M.density[i] = d.density;
    M.viscosity[i] = d.viscosity;
    M.dispersion[i] = d.dispersion;
    M.hardness[i] = d.hardness;
    M.brittleness[i] = d.brittleness;
    M.friction[i] = d.friction;
    M.mass[i] = d.mass;
    M.spawnTemp[i] = d.temp;
    M.conductivity[i] = d.conductivity;
    M.heatCapacity[i] = Math.max(0.05, d.heatCapacity);
    M.ignitionTemp[i] = d.ignitionTemp ?? 32767;
    M.burnTemp[i] = d.burnTemp;
    M.burnDuration[i] = d.burnDuration;
    M.emissive[i] = d.emissive;
    M.meltsAt[i] = d.meltsAt ?? 32767;
    M.meltsInto[i] = ref(d.meltsInto);
    M.freezesAt[i] = d.freezesAt ?? -32768;
    M.freezesInto[i] = ref(d.freezesInto);
    M.boilsAt[i] = d.boilsAt ?? 32767;
    M.boilsInto[i] = ref(d.boilsInto);
    M.condensesAt[i] = d.condensesAt ?? -32768;
    M.condensesInto[i] = ref(d.condensesInto);
    M.ashesInto[i] = ref(d.ashesInto);
    M.electricConductivity[i] = d.electricConductivity;
    M.chargeResistance[i] = d.chargeResistance;
    M.shortCircuits[i] = d.shortCircuits ? 1 : 0;
    M.corrosiveness[i] = d.corrosiveness;
    M.corrosionResist[i] = d.corrosionResist;
    M.toxicity[i] = d.toxicity;
    M.reactivity[i] = d.reactivity;
    M.resonance[i] = d.resonance;
    M.bioActivity[i] = d.bioActivity;
    M.temporalStability[i] = d.temporalStability;
    M.sanctity[i] = d.sanctity;
    M.ruptureThreshold[i] = d.ruptureThreshold;
    M.shatteredInto[i] = ref(d.shatteredInto);
    M.indestructible[i] = d.indestructible ? 1 : 0;
    M.oneWay[i] = d.oneWaySolid === null ? OneWay.NONE : ONE_WAY_CODE[d.oneWaySolid];
    M.lifetime[i] = d.lifetime;
    M.alpha[i] = d.alpha;
    M.grain[i] = d.grain;
    M.refraction[i] = d.refraction;

    const cat = d.category;
    // `M.solid` is what the *cell* sim consults — displacement, liquid flow,
    // gas rise, `place()`. A one-way material must be invisible to all of it:
    // the entire value of a platform is that it adds a floor without adding a
    // wall (docs/10 §2.3), and a floor that dammed water would just be a wall
    // that is harder to reason about. Entity collision is the only consumer
    // that cares, and it reads `M.oneWay` directly (see entity/body.ts).
    M.solid[i] =
      (cat === Category.RIGID || cat === Category.LOOSE) && M.oneWay[i] === OneWay.NONE ? 1 : 0;
    M.mobile[i] =
      cat === Category.LOOSE ||
      cat === Category.LIQUID ||
      cat === Category.GAS ||
      cat === Category.SLUDGE ||
      cat === Category.PLASMA
        ? 1
        : 0;
    // Gases and thin liquids barely occlude light; solids fully occlude.
    M.opacity[i] =
      cat === Category.VOID || cat === Category.FIELD
        ? 0
        : cat === Category.GAS
          ? 0.18 * d.alpha
          : cat === Category.PLASMA
            ? 0.1
            : cat === Category.LIQUID
              ? 0.45 * d.alpha
              : 1;
    // Which materials the light field seeds from, and therefore which chunks it
    // has to build tiles for at all. Thresholded rather than `> 0` because two
    // dozen materials carry a decorative 0.05–0.2 emissive for the shader's
    // glow ramp; admitting those would mean every chunk of mercury or fungus in
    // the world is a light source, which is the unbounded-lit-set failure the
    // field is bounded to avoid. Everything docs/01 §6.3 names as a source —
    // fire, lava, radiant matter, plasma, ember grit, molten iron — is at 0.4
    // or above.
    M.lightSource[i] = d.emissive >= LIGHT_SOURCE_MIN ? 1 : 0;

    M.palette[i * 4 + 0] = d.color.r;
    M.palette[i * 4 + 1] = d.color.g;
    M.palette[i * 4 + 2] = d.color.b;
    M.palette[i * 4 + 3] = Math.round(d.alpha * 255);
  }

  // VOID must be transparent and non-solid regardless of what was declared.
  M.palette[3] = 0;
  M.solid[0] = 0;
  M.mobile[0] = 0;
  M.opacity[0] = 0;
  M.lightSource[0] = 0;
  M.oneWay[0] = OneWay.NONE;

  finalized = true;
}

export function isFinalized(): boolean {
  return finalized;
}

/** Test-only. */
export function _resetMaterials(): void {
  defs.length = 0;
  byName.clear();
  finalized = false;
}
